import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { visualizacaoModoPipeline } from '../../../../../common/visualizacao-modo';
import { ApiError, AuthService, BaseVisualizacaoService, ConfigService, DragDropService, MessageService } from '../../../../../core';
import { ConfigCampanha } from '../../../../configuracoes/app/campanhas';
import { UsuarioPerfil } from '../../../../usuario/usuario-perfil';
import { CampanhaAnaliseAuthGuardService, CampanhaAnaliseHttpService } from '../../analise';
import { CampanhaEnviarAnaliseFormModalComponent } from '../../analise/enviar-analise-form-modal';
import { CampanhaOptionsActions } from '../../campanha-options';
import { CampanhaSearch } from '../../campanha-search';
import { CampanhaSearchOrderBy } from '../../campanha-search-orderby';
import { CampanhaSearchView } from '../../campanha-search-view';
import { CampanhaEncerramentoAuthGuardService, CampanhaEncerramentoHttpService } from '../../encerramento';
import { CampanhaFilter } from '../../filter';
import { CampanhaCrudHttpService, CampanhaSearchHttpService } from '../../services';
import { campanhaStatusEmAnalise, campanhaStatusEmAndamento, campanhaStatusEncerrada, isStatusEmAnalise, isStatusEmAndamento } from '../../status';
import { CampanhaVisualizarModalComponent } from '../../visualizar-modal';
import { CampanhaPipeline } from './campanha-pipeline';
import { CampanhaPipelineList } from './campanha-pipeline-list';
import { CampanhaPipelineCardComponent } from './card';

@Component({
    selector: 'sgt-campanha-pipeline',
    templateUrl: './campanha-pipeline.component.html',
    styleUrls: ['./campanha-pipeline.component.less']
})
export class CampanhaPipelineComponent implements OnInit, OnDestroy {
    configCampanha: ConfigCampanha;
    perfilUsuario: UsuarioPerfil;

    @Input() filter: CampanhaFilter;
    @Input() optionsVisibility: CampanhaOptionsActions = {
        canEdit: true,
        canVisualizar: true,
        canDelete: true,
        // Análise
        canSolicitarAnalise: true,
        canAprovarAnalise: true,
        canReprovarAnalise: true,
        // Encerramento
        canEncerrar: true,
        // Pedidos
        canLiberarPedidos: true
    }

    // Pipeline
    pipeline: CampanhaPipeline;
    @ViewChild('pipelineComponent', {static: true}) pipelineComponent: ElementRef;
    @ViewChildren('pipelineCard') pipelineCards: QueryList<CampanhaPipelineCardComponent>;

    // Search
    orderByColumn = CampanhaSearchOrderBy.Numero;
    orderByDir = 'desc';
    private searchSubs$: Subscription;

    // Análise
    @ViewChild('enviarAnaliseFormModal') enviarAnaliseFormModal: CampanhaEnviarAnaliseFormModalComponent;
    // Visualizar Campanha
    @ViewChild('visualizarModal') visualizarModal: CampanhaVisualizarModalComponent;

    constructor(private activatedRoute: ActivatedRoute,
                private authService: AuthService,
                private baseVisualizacaoService: BaseVisualizacaoService,
                private configService: ConfigService,
                private messageService: MessageService,
                private dragDropService: DragDropService,
                private campanhaSearchHttpService: CampanhaSearchHttpService,
                private campanhaCrudHttpService: CampanhaCrudHttpService,
                private campanhaAnaliseAuthGuardService: CampanhaAnaliseAuthGuardService,
                private campanhaAnaliseHttpService: CampanhaAnaliseHttpService,
                private campanhaEncerramentoAuthGuardService: CampanhaEncerramentoAuthGuardService,
                private campanhaEncerramentoHttpService: CampanhaEncerramentoHttpService) { }

    ngOnInit(): void {
        this.configCampanha = this.configService.getCampanha();
        this.perfilUsuario = this.authService.getPerfilUsuario();
    }

    async search() {
        try {
            this.searchSubs$?.unsubscribe();
            this.searchSubs$ = new Subscription();

            // Search View
            const searchView = await this.getSearchView();
            this.pipeline = new CampanhaPipeline(searchView, jQuery(this.pipelineComponent.nativeElement).outerHeight(true), this.configCampanha.roiPontoEquilibrio);

            // Search Entities
            this.pipeline.lists.forEach(list => {
                this.searchEntities(list);
            });
        } catch (error) {
            this.messageService.error('Ocorreu um erro ao pesquisar. Tente novamente', 'Erro ao pesquisar', error);
        }
    }

    private getSearchView(): Promise<CampanhaSearchView> {
        return new Promise<CampanhaSearchView>((resolve, reject) => {
            this.searchSubs$.add(this.campanhaSearchHttpService.searchView(this.filter, this.orderByColumn, this.orderByDir)
                .pipe(
                    take(1)
                )
                .subscribe(
                    searchView => {
                        if (!searchView) {
                            reject('Nenhum status foi encontrado para exibir as campanhas. Tente novamente e se o erro persistir entre em contato com o suporte')
                            return;
                        }

                        resolve(searchView);
                    },
                    (error: ApiError) => reject(error)
                ));
        });
    }

    searchEntities(list: CampanhaPipelineList) {
        if (!list) {
            return;
        }

        list.isLoading = true;
        list.currentSub$?.unsubscribe();
        list.currentSub$ = this.campanhaSearchHttpService.search(visualizacaoModoPipeline, list.getMoreEntities(), [list.status.id], this.filter, this.orderByColumn, this.orderByDir, -1, -1)
            .pipe(
                take(1)
            )
            .subscribe(
                entities => {
                    list.addEntities(entities);
                },
                (error: ApiError) => this.messageService.error(error.cause, list.searchMessageError, error),
                () => list.isLoading = false
            );
    }

    /** Pesquisa a campanha informada */
    private searchOneEntity(idCampanha: number, status: number = null): Promise<CampanhaSearch> {
        return new Promise<CampanhaSearch>((resolve, reject) => {
            this.campanhaSearchHttpService.searchOne(idCampanha, this.filter, status)
                .pipe(
                    take(1)
                )
                .subscribe(
                    oportunidade => resolve(oportunidade),
                    (error: ApiError) => {
                        this.messageService.error(error.message, error.cause, error);
                        reject(error);
                    }
                );
        });
    }

    toggleList(list: CampanhaPipelineList) {
        if (!list) {
            return;
        }
        list.isCollapsed = !list.isCollapsed;
    }

    /** Abre/Fecha os collapses de todos os cards da list */
    toggleCardsCollapses(list: CampanhaPipelineList, listIndex: number, openCardsCollapses: boolean) {
        list.isCardsCollapsesOpening = true;
        // Abrindo/fechando os collapses dos cards
        setTimeout(() => {
            this.pipelineCards?.forEach(pipelineCard => {
                if (pipelineCard.listIndex === listIndex) { // Somente abre/fecha os collapse dos cards que contém o index da list igual ao index informado
                    if (!list.isCardsCollapsesOpened) {
                        pipelineCard.openCollapse();
                    } else {
                        pipelineCard.closeCollpase();
                    }
                }
            });
            list.isCardsCollapsesOpening = false;

            // Atualizando a variável de controle de abertura dos collapses dos cards
            list.isCardsCollapsesOpened = openCardsCollapses;
        }, list.entities?.length > 30 ? 100 : 0);
    }

    onDragStarted() {
        this.pipeline.isDragging = true;
    }

    onDragDropped() {
        this.pipeline.isDragging = false;
    }

    canDragAndDrop(entity: CampanhaSearch): boolean {
        return false;
    }

    async drop(dropEvent: CdkDragDrop<CampanhaSearch[]>, list: CampanhaPipelineList) {
        try {
            if (this.dragDropService.isSameDropList(dropEvent)) {
                this.dragDropService.dropCancel(dropEvent);
                return;
            }
            const entity = dropEvent.item?.data as CampanhaSearch;

            if (!this.canDragAndDrop(entity)) {
                this.messageService.info('Não é possível mover a campanha para o status desejado', 'Informação');
                this.dragDropService.dropCancel(dropEvent);
                return;
            }

            if (list.isStatusNaoConfirmada) { // Não Confirmada
                // Nada Faz
            } else if (list.isStatusEmAnalise) { // Em Análise
                // Nada Faz
            } else if (list.isStatusEmAndamento) { // Em Andamento
                // Nada Faz
            } else if (list.isStatusReprovada) { // Reprovada
                // Nada Faz
            } else if (list.isStatusEmRevisao) { // Em Revisão
                // Nada Faz
            } else if (list.isStatusEncerrada) { // Encerrada
                // Nada Faz
            }

            this.afterDrop(entity, dropEvent, list);
        } catch (error) {
            this.messageService.error(error.message, error.cause, error);
            this.dragDropService.dropCancel(dropEvent);
            this.pipeline.dropEntity(dropEvent.container.id, dropEvent.previousContainer.id, dropEvent.item?.data as CampanhaSearch);
        }
    }

    private afterDrop(entity: CampanhaSearch, dropEvent: CdkDragDrop<CampanhaSearch[]>, list: CampanhaPipelineList) {
        list.updateOneEntity(entity, null, this.pipeline.roiPontoEquilibrio);
        this.pipeline.dropEntity(dropEvent.previousContainer.id, dropEvent.container.id, entity);
    }

    edit(entity: CampanhaSearch, list: CampanhaPipelineList) {
        this.pipeline.setEntityEditing(entity);
        this.pipeline.setListEditing(list);
        this.baseVisualizacaoService.edit(entity.id, this.activatedRoute);
    }

    delete(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }

        this.messageService.confirm(`Deseja realmente excluir a de campanha ${entity.nome}?`).then(result => {
            if (result) {
                this.campanhaCrudHttpService.delete(entity.id)
                    .subscribe(
                        () => {
                            this.messageService.success('', `Campanha ${entity.nome} excluída com sucesso`);
                            this.pipeline.removeOneEntity(entity);
                        },
                        (error: ApiError) => {
                            this.messageService.error(error.message, error.cause, error);
                        }
                    );
            }
        });
    }

    /** Solicita a análise da campanha */
    solicitarAnalise(entity: CampanhaSearch, list: CampanhaPipelineList) {
        if (!entity) {
            return;
        }
        if (!this.campanhaAnaliseAuthGuardService.canSolicitar(entity.id, entity.status, entity.idUsuarioCreated)) {
            this.messageService.info(`Você não possui permissão para solicitar a análise da campanha ${entity?.nome ?? ''}`, 'Sem permissão para solicitar análise');
            return false;
        }

        this.messageService.confirm(`Deseja realmente solicitar a análise da campanha ${entity.nome}?`).then(result => {
            if (result) {
                this.campanhaAnaliseHttpService.solicitar(entity.id)
                    .subscribe(
                        entityResponse => {
                            this.messageService.success('', `Análise da campanha ${entity.nome} solicitada com sucesso`);
                            // Movendo a campanha da list de Não Confirmada para Em Análise
                            this.pipeline.setEntityEditing(entity);
                            this.pipeline.setListEditing(list);
                            this.pipeline.moveEntity(entityResponse);
                        },
                        (error: ApiError) => {
                            this.messageService.error(error.message, error.cause, error);
                        }
                    );
            }
        });
    }

    /** Realiza a aprovação da análise da campanha */
    aprovarAnalise(entity: CampanhaSearch, list: CampanhaPipelineList) {
        if (!entity) {
            return;
        }
        if (!this.campanhaAnaliseAuthGuardService.canEnviar(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0)) {
            this.messageService.info(`Você não possui permissão para aprovar a análise da campanha ${entity?.nome ?? ''}`, 'Sem permissão para aprovar a análise');
            return false;
        }

        this.pipeline.setEntityEditing(entity);
        this.pipeline.setListEditing(list);
        this.enviarAnaliseFormModal.aprovar(entity.id);
    }

    /** Realiza a reprovação da análise da campanha */
    reprovarAnalise(entity: CampanhaSearch, list: CampanhaPipelineList) {
        if (!entity) {
            return;
        }
        if (!this.campanhaAnaliseAuthGuardService.canEnviar(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0)) {
            this.messageService.info(`Você não possui permissão para reprovar a análise da campanha ${entity?.nome ?? ''}`, 'Sem permissão para reprovar a análise');
            return false;
        }

        this.pipeline.setEntityEditing(entity);
        this.pipeline.setListEditing(list);
        this.enviarAnaliseFormModal.reprovar(entity.id);
    }

    /** Após enviar a análise */
    onAfterEnviarAnalise(entity: CampanhaSearch) {
        this.pipeline.moveEntity(entity);
    }

    /** Encerra a campanha */
    encerrar(entity: CampanhaSearch, list: CampanhaPipelineList) {
        if (!entity) {
            return;
        }
        if (!this.campanhaEncerramentoAuthGuardService.canEncerrar(entity.id, entity.status)) {
            this.messageService.info(`Você não possui permissão para encerrar a campanha ${entity?.nome ?? ''}`, 'Sem permissão para encerrar a campanha');
            return false;
        }

        this.messageService.confirm(`Ao encerrar a campanha a mesma não poderá ser mais utilizada.\n\nDeseja realmente encerrar a campanha ${entity.nome}?`).then(result => {
            if (result) {
                this.campanhaEncerramentoHttpService.encerrar(entity.id)
                    .subscribe(
                        entityResponse => {
                            this.messageService.success('', `Campanha ${entity.nome} encerrada com sucesso`);
                            // Movendo a campanha da list de Em Andamento para Encerrada
                            this.pipeline.setEntityEditing(entity);
                            this.pipeline.setListEditing(list);
                            this.pipeline.moveEntity(entityResponse);
                        },
                        (error: ApiError) => {
                            this.messageService.error(error.message, error.cause, error);
                        }
                    );
            }
        });
    }

    /** Abre o modal para visualizar a campanha */
    visualizar(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }

        this.visualizarModal.open(entity.id, this.filter, entity.status);
    }

    /** Abre o modal para liberar os pedidos da campanha */
    liberarPedidos(entity: CampanhaSearch, list: CampanhaPipelineList) {
        if (!entity) {
            return;
        }

        this.pipeline.setEntityEditing(entity);
        this.pipeline.setListEditing(list);
        this.visualizarModal.open(entity.id, this.filter, campanhaStatusEmAnalise.id, true);
    }

    /** Após liberar pedido */
    async afterLiberarPedidos() {
        const entity = this.pipeline.getEntityEditing();
        if (!entity) {
            return;
        }

        // Em Análise
        const newEntityEmAnalise = await this.searchOneEntity(entity.id, campanhaStatusEmAnalise.id);
        if (newEntityEmAnalise) { // Atualizando a campanha
            this.pipeline.updateEntity(newEntityEmAnalise);
        } else { // Removendo a campanha da list de Em Análise
            this.pipeline.removeOneEntity(entity);
        }

        // Em Andamento
        const newEntityEmAndamento = await this.searchOneEntity(entity.id, campanhaStatusEmAndamento.id);
        if (newEntityEmAndamento) { // Atualizando campanha Em Andamento
            const listEmAndamento = this.pipeline.getListEmAndamento();
            const entityOld = listEmAndamento.getEntity(newEntityEmAndamento.id);
            listEmAndamento.updateOneEntity(newEntityEmAndamento, entityOld, this.pipeline.roiPontoEquilibrio);
        }

        // Encerrada
        const newEntityEncerrada = await this.searchOneEntity(entity.id, campanhaStatusEncerrada.id);
        if (newEntityEncerrada) { // Atualizando campanha Encerrada
            const listEncerrada = this.pipeline.getListEncerrada();
            const entityOld = listEncerrada.getEntity(newEntityEncerrada.id);
            listEncerrada.updateOneEntity(newEntityEncerrada, entityOld, this.pipeline.roiPontoEquilibrio);
        }

        // Recalculando os totais do pipeline
        this.pipeline.calcularTotal();
    }

    ngOnDestroy() {
        // Realizando unsubscribe dos subscriptions das pesquisas
        this.searchSubs$?.unsubscribe();
    }
}
